import React, { useState, useMemo } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';

const MAX_DISPLAY = 50;

interface CityComboboxProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  cities: string[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

export const CityCombobox: React.FC<CityComboboxProps> = ({
  id,
  value,
  onChange,
  cities,
  placeholder = 'Type to search cities...',
  disabled = false,
  error,
}) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query) return cities.slice(0, MAX_DISPLAY);
    const lower = query.toLowerCase();
    const matches = cities.filter((c) => c.toLowerCase().includes(lower));
    return matches.slice(0, MAX_DISPLAY);
  }, [cities, query]);

  const hasError = !!error;

  return (
    <div className="w-full">
      <Combobox
        value={value}
        onChange={(selected: string | null) => {
          onChange(selected ?? '');
          setQuery('');
        }}
        disabled={disabled}
      >
        {({ open }) => (
          <div className="relative">
            <div
              className={`
                relative w-full overflow-hidden rounded-md border bg-white text-left
                transition-colors focus-within:ring-2 focus-within:ring-offset-1
                ${hasError
                  ? 'border-red-500 focus-within:ring-red-500'
                  : 'border-gray-300 focus-within:border-indigo-500 focus-within:ring-indigo-500'
                }
                ${disabled ? 'cursor-not-allowed opacity-50' : ''}
              `}
            >
              <Combobox.Input
                id={id}
                className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 bg-transparent disabled:cursor-not-allowed"
                displayValue={(city: string) => city}
                onChange={(event) => {
                  setQuery(event.target.value);
                  onChange(event.target.value);
                }}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete="off"
              />
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </Combobox.Button>
            </div>

            <Transition
              show={open}
              as={React.Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {filtered.length === 0 ? (
                  <div className="relative cursor-default select-none py-2 px-4 text-gray-500 text-sm">
                    No cities found.
                  </div>
                ) : (
                  filtered.map((city) => (
                    <Combobox.Option
                      key={city}
                      value={city}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? 'font-semibold' : 'font-normal'
                            }`}
                          >
                            {city}
                          </span>
                          {selected && (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                active ? 'text-white' : 'text-indigo-600'
                              }`}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
        )}
      </Combobox>

      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

CityCombobox.displayName = 'CityCombobox';
